import React from 'react';
import { DEFAULT_READER_DISPLAY_SETTINGS, ReaderDisplaySettings } from './ReaderDisplaySettings';

const parseBold = (text: string) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <span key={index} className="font-bold text-slate-900">
          {part.slice(2, -2)}
        </span>
      );
    }
    return part;
  });
};

function isQuoteLine(raw: string): boolean {
  return /^>\s?/.test(String(raw || '').trim());
}

function normalizeQuoteLine(raw: string): string {
  const trimmed = String(raw || '').trim();
  return trimmed
    .replace(/^>\s?/, '')
    .replace(/\[!.*?\]\s*/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .trim();
}

interface TableRendererProps {
  lines: string[];
  settings: ReaderDisplaySettings;
}

const TableRenderer: React.FC<TableRendererProps> = ({ lines, settings }) => {
  const rows = lines.map((line) => line.split('|').slice(1, -1).map((cell) => cell.trim()));
  if (rows.length < 2) return null;

  const header = rows[0];
  const body = rows.slice(2);
  const tableFontSize = Math.max(12, settings.fontSize - 1);

  return (
    <div
      className="overflow-x-auto rounded-lg border border-slate-200 shadow-sm"
      style={{ marginTop: settings.paragraphSpacing, marginBottom: settings.paragraphSpacing }}
    >
      <table className="w-full text-left" style={{ fontSize: `${tableFontSize}px` }}>
        <thead className="text-slate-700 uppercase bg-slate-50 border-b border-slate-200">
          <tr>
            {header.map((cell, index) => (
              <th key={index} className="px-3 sm:px-5 py-2.5 sm:py-3 whitespace-nowrap font-bold">
                {parseBold(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, rowIndex) => (
            <tr key={rowIndex} className="bg-white border-b border-slate-100 hover:bg-slate-50 last:border-none">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 sm:px-5 py-2.5 sm:py-3.5 text-slate-700">
                  {parseBold(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface MarkdownRendererProps {
  content: string;
  settings?: ReaderDisplaySettings;
  className?: string;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  settings = DEFAULT_READER_DISPLAY_SETTINGS,
  className = '',
}) => {
  if (!content) return null;

  const lines = content.trim().split('\n');
  const elements: React.ReactNode[] = [];
  const headingScale = settings.fontSize / DEFAULT_READER_DISPLAY_SETTINGS.fontSize;

  const paragraphStyle: React.CSSProperties = {
    fontSize: `${settings.fontSize}px`,
    lineHeight: settings.lineHeight,
    marginTop: `${Math.max(4, settings.paragraphSpacing / 2)}px`,
    marginBottom: `${Math.max(4, settings.paragraphSpacing / 2)}px`,
  };

  const heading1Size = Math.round(30 * headingScale);
  const heading2Size = Math.round(24 * headingScale);
  const heading3Size = Math.round(20 * headingScale);
  const heading4Size = Math.round(18 * headingScale);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === '') {
      // 统一忽略手动空行，避免段落间距不一致。
      continue;
    }

    if (trimmed.startsWith('|')) {
      const tableLines = [trimmed];
      while (i + 1 < lines.length && lines[i + 1].trim().startsWith('|')) {
        i += 1;
        tableLines.push(lines[i].trim());
      }
      elements.push(<TableRenderer key={`table-${i}`} lines={tableLines} settings={settings} />);
      continue;
    }

    if (trimmed === '---') {
      elements.push(
        <hr
          key={i}
          className="border-slate-200"
          style={{ marginTop: settings.paragraphSpacing * 1.4, marginBottom: settings.paragraphSpacing * 1.4 }}
        />,
      );
      continue;
    }

    if (trimmed.startsWith('# ')) {
      elements.push(
        <h1
          key={i}
          className="font-bold text-slate-900 mt-8 mb-5 pb-3 border-b border-slate-100"
          style={{ fontSize: `${heading1Size}px`, lineHeight: 1.3 }}
        >
          {trimmed.replace('# ', '')}
        </h1>,
      );
      continue;
    }

    if (trimmed.startsWith('## ')) {
      elements.push(
        <h2
          key={i}
          className="font-bold text-slate-800 mt-8 mb-4 flex items-center gap-2"
          style={{ fontSize: `${heading2Size}px`, lineHeight: 1.34 }}
        >
          <span className="w-1 h-5 bg-indigo-600 rounded-full" />
          {trimmed.replace('## ', '')}
        </h2>,
      );
      continue;
    }

    if (trimmed.startsWith('### ')) {
      elements.push(
        <h3
          key={i}
          className="font-bold text-slate-800 mt-6 mb-3"
          style={{ fontSize: `${heading3Size}px`, lineHeight: 1.35 }}
        >
          {trimmed.replace('### ', '')}
        </h3>,
      );
      continue;
    }

    if (trimmed.startsWith('#### ')) {
      elements.push(
        <h4
          key={i}
          className="font-bold text-slate-700 mt-5 mb-2 flex items-center gap-2"
          style={{ fontSize: `${heading4Size}px`, lineHeight: 1.36 }}
        >
          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full" />
          {trimmed.replace('#### ', '')}
        </h4>,
      );
      continue;
    }

    if (trimmed.startsWith('- ')) {
      const items = [trimmed.substring(2)];
      while (i + 1 < lines.length && lines[i + 1].trim().startsWith('- ')) {
        i += 1;
        items.push(lines[i].trim().substring(2));
      }
      elements.push(
        <ul
          key={`list-${i}`}
          className="ml-5 list-disc marker:text-indigo-500"
          style={{
            marginTop: settings.paragraphSpacing / 2,
            marginBottom: settings.paragraphSpacing / 2,
          }}
        >
          {items.map((item, index) => (
            <li
              key={`${i}-${index}`}
              className="text-slate-700"
              style={{
                fontSize: `${settings.fontSize}px`,
                lineHeight: settings.lineHeight,
                marginTop: index === 0 ? 0 : Math.max(2, settings.paragraphSpacing / 4),
              }}
            >
              {parseBold(item)}
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    if (isQuoteLine(trimmed)) {
      const quoteParagraphs: string[] = [];
      let paragraphBuffer: string[] = [];
      const startIndex = i;

      const flushParagraphBuffer = () => {
        if (paragraphBuffer.length === 0) return;
        const paragraph = paragraphBuffer.join(' ').trim();
        if (paragraph) quoteParagraphs.push(paragraph);
        paragraphBuffer = [];
      };

      let j = i;
      while (j < lines.length) {
        const currentRaw = lines[j];
        const currentTrimmed = currentRaw.trim();

        if (isQuoteLine(currentTrimmed)) {
          const quoteText = normalizeQuoteLine(currentRaw);
          if (quoteText) {
            paragraphBuffer.push(quoteText);
          } else {
            flushParagraphBuffer();
          }
          j += 1;
          continue;
        }

        if (
          currentTrimmed === ''
          && j + 1 < lines.length
          && isQuoteLine(lines[j + 1].trim())
        ) {
          flushParagraphBuffer();
          j += 1;
          continue;
        }

        break;
      }

      flushParagraphBuffer();
      i = j - 1;

      if (quoteParagraphs.length === 0) {
        continue;
      }

      elements.push(
        <blockquote
          key={`quote-${startIndex}`}
          className="relative overflow-hidden rounded-xl border border-amber-200/70 bg-gradient-to-r from-amber-50/90 via-amber-50/40 to-white px-3 py-3 text-slate-700 shadow-[0_1px_0_rgba(251,191,36,0.12)]"
          style={{
            fontSize: `${settings.fontSize}px`,
            lineHeight: settings.lineHeight,
            marginTop: `${Math.max(6, settings.paragraphSpacing / 2)}px`,
            marginBottom: `${Math.max(6, settings.paragraphSpacing / 2)}px`,
          }}
        >
          <span className="absolute left-0 top-0 h-full w-1.5 bg-gradient-to-b from-amber-400 to-amber-300" />
          <span className="pointer-events-none absolute right-3 top-2 text-amber-300/50 text-lg font-serif">”</span>
          {quoteParagraphs.map((paragraph, idx) => (
            <p
              key={`quote-${startIndex}-${idx}`}
              className="relative italic"
              style={idx === 0 ? undefined : { marginTop: `${Math.max(7, settings.paragraphSpacing / 2)}px` }}
            >
              {parseBold(paragraph)}
            </p>
          ))}
        </blockquote>,
      );
      continue;
    }

    elements.push(
      <p key={i} className="text-slate-700" style={paragraphStyle}>
        {parseBold(trimmed)}
      </p>,
    );
  }

  return <article className={`font-sans ${className}`.trim()}>{elements}</article>;
};
